import './ApplicationSelectDropdown.css';
import React, { FC, useContext } from 'react';
import { observer } from 'mobx-react-lite';
import cx from "classnames";
import icons from '../../../icons';
import { Dropdown, DropdownContext, DropdownItem } from "../../../components/Dropdown";
import { EditableLabel } from "../../../components/EditableLabel";
import { ApplicationContextMenu } from "./ApplicationContextMenu";
import { ApplicationTypes } from '../../../machinelogic/constants/ApplicationTypes';
import { Application, ApplicationBase, Library, User } from '../../../machinelogic/types';

interface DropdownButtonProps {
  isEditingApplicationName: boolean;
  setIsEditingApplicationName: (value: boolean) => void;
  library: Library;
}

const DropdownButton: FC<DropdownButtonProps> = observer(({ isEditingApplicationName, setIsEditingApplicationName, library}) => {
  const { isDropdownOpen } = useContext(DropdownContext);
  const chevronIcon = isDropdownOpen ? icons.chevronUp : icons.chevronDown;
  const currentApplication = library.currentApplication;

  return (
    <>
      <i className={ icons.library }></i>
      <EditableLabel
        isEditing={isEditingApplicationName}
        setIsEditing={setIsEditingApplicationName}
        label={<span className="application-name text-ellipsis">{library.currentApplication?.name || 'Select application'}</span>}
        value={currentApplication?.name || ''}
        setValue={(value) => currentApplication?.setName(value)}
      />
      <span className="pull-right">
        { currentApplication?.isReadOnly() && <span className="read-only">Read Only</span> }
        <i className={`${chevronIcon} dropdown-caret`}></i>
      </span>
    </>
  );
});

interface ApplicationSelectDropdownProps {
  library: Library;
  isRunning: boolean;
  onAppSelected?: ((app: ApplicationBase) => void) | undefined;
  onApplicationUploaded: (app: Application) => void;
  applicationType: typeof ApplicationTypes[keyof typeof ApplicationTypes]
  user: User | undefined;
  allowAppCreation: boolean
}

export const ApplicationSelectDropdown: FC<ApplicationSelectDropdownProps> = observer(({library, onAppSelected, onApplicationUploaded, isRunning, applicationType, user, allowAppCreation}) => {
  const [isEditingApplicationName, setIsEditingApplicationName] = React.useState(false);
  const currentApplication = library.currentApplication;
  const applications = library
    .getAllApplications()
    .filter(application => application.getType() === applicationType);

  const selectApplication = (app: ApplicationBase) => {
    if (app.id === currentApplication?.id) return;

    library.selectApplication(app, true);
    onAppSelected?.(app);
  };

  const createNewApplication = () => {
    const newApp = library.newApplication(applicationType);
    library.selectApplication(newApp, true);
  };

  return (
    <div className="application-select-container">
      <Dropdown
        className={cx('application-select-dropdown', {
          'is-editing': isEditingApplicationName,
        })}
        button={
          <DropdownButton
            isEditingApplicationName={ isEditingApplicationName }
            setIsEditingApplicationName={ setIsEditingApplicationName }
            library={library}
          />
        }
        disabled={ isRunning || isEditingApplicationName }
      >
        {
          allowAppCreation &&
          <DropdownItem onClick={ createNewApplication }>
            <i className={ icons.add_application }></i>
            <span className="item-name text-ellipsis">Create new custom app</span>
          </DropdownItem>
        }
        { applications.map((app: ApplicationBase) => (
          <DropdownItem
            key={ app.id }
            isSelected={app.id === currentApplication?.id}
            onClick={ () => selectApplication(app) }>
            <span className="item-name text-ellipsis">{ app.name }</span>
            { app.isReadOnly() && <span className="read-only pull-right">Read Only</span> }
          </DropdownItem>
        )) }

      </Dropdown>
      <ApplicationContextMenu
        onApplicationRename={() => setIsEditingApplicationName(!isEditingApplicationName)}
        onApplicationUploaded={onApplicationUploaded}
        library={library}
        onAppSelected={onAppSelected}
        isRunning={isRunning}
        applicationType={applicationType}
        user={user}
      />
    </div>
  );
});